import { ReactNode, createContext, useCallback, useMemo, useState } from "react";

import { Session } from "@/shared/lib/types/auth";
import { deleteSession, getSession, setSession } from "@/shared/lib/utils/session";

interface AuthenticationContextType {
  session: Session | null;
  isAuthenticated: boolean;
  authenticate: (accessToken: string) => void;
  deauthenticate: () => void;
}

export const AuthenticationContext = createContext<AuthenticationContextType | null>(null);

export function AuthenticationProvider({ children }: { children: ReactNode }) {
  const [sessionState, setSessionState] = useState<Session | null>(() => getSession());

  const authenticate = useCallback((accessToken: string) => {
    setSession(accessToken);

    const session = getSession();
    setSessionState(session);
  }, []);

  const deauthenticate = useCallback(() => {
    deleteSession();
    setSessionState(null);
  }, []);

  const value = useMemo(
    () => ({
      session: sessionState,
      isAuthenticated: !!sessionState,
      authenticate,
      deauthenticate,
    }),
    [sessionState, authenticate, deauthenticate]
  );

  return <AuthenticationContext.Provider value={value}>{children}</AuthenticationContext.Provider>;
}
